import { ROLE_PERMISSIONS, type Permission, type User } from '@burmazon/shared';
import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { useAdminAuthStore, ACTIVITY_KEY } from './store/auth';

const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductFormPage = lazy(() => import('./pages/ProductFormPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const OrderPrintPage = lazy(() => import('./pages/OrderPrintPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const CustomerDetailPage = lazy(() => import('./pages/CustomerDetailPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const PosPage = lazy(() => import('./pages/PosPage'));
const DiscountsPage = lazy(() => import('./pages/DiscountsPage'));
const DiscountFormPage = lazy(() => import('./pages/DiscountFormPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const UserFormPage = lazy(() => import('./pages/UserFormPage'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const SuppliersPage = lazy(() => import('./pages/suppliers/SuppliersPage'));
const SupplierFormPage = lazy(() => import('./pages/suppliers/SupplierFormPage'));
const SupplierDetailPage = lazy(() => import('./pages/suppliers/SupplierDetailPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/suppliers/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazy(() => import('./pages/suppliers/PurchaseOrderFormPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/suppliers/PurchaseOrderDetailPage'));
const BannersPage = lazy(() => import('./pages/BannersPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ContactSubmissionsPage = lazy(() => import('./pages/ContactSubmissionsPage'));
const ProfitLossPage = lazy(() =>
  import('./pages/ProfitLossPage').then((m) => ({ default: m.ProfitLossPage })),
);

function DefaultRedirect(): React.ReactElement {
  const user = useAdminAuthStore((s) => s.user);
  return <Navigate to={getDefaultRoute(user)} replace />;
}

function getDefaultRoute(user: Omit<User, 'createdAt' | 'updatedAt'> | null): string {
  if (!user) return '/login';
  const granted: readonly string[] = user.customRoleId
    ? (user.customRolePermissions ?? [])
    : (ROLE_PERMISSIONS[user.role] as readonly string[]);
  if (granted.includes('dashboard:view')) return '/dashboard';
  if (granted.includes('orders:view')) return '/orders';
  if (granted.includes('products:view')) return '/products';
  if (granted.includes('inventory:view')) return '/inventory';
  if (granted.includes('customers:view')) return '/customers';
  if (granted.includes('pos:access')) return '/pos';
  if (granted.includes('discounts:view')) return '/discounts';
  if (granted.includes('reports:view')) return '/analytics';
  if (granted.includes('users:view')) return '/users';
  if (granted.includes('settings:view')) return '/settings';
  return '/settings';
}

function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: React.ReactNode;
}): React.ReactElement {
  const user = useAdminAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  const granted = user.customRoleId
    ? (user.customRolePermissions ?? [])
    : (ROLE_PERMISSIONS[user.role] as readonly string[]);
  if (!granted.includes(permission)) return <Navigate to={getDefaultRoute(user)} replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }): React.ReactElement {
  const isAuthenticated = useAdminAuthStore((s) => s.isAuthenticated);
  const restoreSession = useAdminAuthStore((s) => s.restoreSession);
  const [ready, setReady] = useState(false);

  // Keep last-activity timestamp fresh so the 30-min idle check in
  // restoreSession() knows whether the user was recently active.
  useEffect(() => {
    const update = () => localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, update, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, update));
  }, []);

  useEffect(() => {
    void restoreSession().finally(() => setReady(true));
  }, [restoreSession]);

  if (!ready) return <PageFallback />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Print page — auth-guarded but no admin layout */}
          <Route
            path="/orders/:id/print"
            element={
              <RequireAdmin>
                <OrderPrintPage />
              </RequireAdmin>
            }
          />

          {/* Admin app */}
          <Route
            path="/"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<DefaultRedirect />} />
            <Route
              path="dashboard"
              element={
                <RequirePermission permission="dashboard:view">
                  <DashboardPage />
                </RequirePermission>
              }
            />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:id" element={<OrderDetailPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/new" element={<ProductFormPage />} />
            <Route path="products/:id" element={<ProductDetailPage />} />
            <Route path="products/:id/edit" element={<ProductFormPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="contact-submissions" element={<ContactSubmissionsPage />} />
            <Route path="pos" element={<PosPage />} />
            <Route path="discounts" element={<DiscountsPage />} />
            <Route path="discounts/new" element={<DiscountFormPage />} />
            <Route path="discounts/:id/edit" element={<DiscountFormPage />} />
            <Route
              path="analytics"
              element={
                <RequirePermission permission="reports:view">
                  <AnalyticsPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports"
              element={
                <RequirePermission permission="reports:view">
                  <ReportsPage />
                </RequirePermission>
              }
            />
            <Route
              path="profit-loss"
              element={
                <RequirePermission permission="reports:view">
                  <ProfitLossPage />
                </RequirePermission>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="suppliers/new" element={<SupplierFormPage />} />
            <Route path="suppliers/:id" element={<SupplierDetailPage />} />
            <Route path="suppliers/:id/edit" element={<SupplierFormPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="purchase-orders/new" element={<PurchaseOrderFormPage />} />
            <Route path="purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
            <Route path="purchase-orders/:id/edit" element={<PurchaseOrderFormPage />} />
            <Route path="banners" element={<BannersPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/new" element={<UserFormPage />} />
            <Route path="users/:id/edit" element={<UserFormPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
